import { AGREEMENT_TYPE_OPTIONS } from '@config/options.js'
import { hostUrl } from '@composables/useWindowEvents.js'
import { Settings } from '@config/game.js'
import { MODIO_SITE } from '@config'
import { gameStore } from '@stores'
import DOMPurify from 'dompurify'

const game = gameStore().getPublicGame()

DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noreferrer noopener')
  }
})

function sanitize(text) {
  return DOMPurify.sanitize(text, {
    ADD_ATTR: ['target', 'frameborder', 'allowfullscreen'],
    ADD_TAGS: ['iframe'],
  })
}

export default function format(input) {
  // Extract mentions
  const mentionRegex = new RegExp(
    `<a href="${MODIO_SITE}/(u|g)/[\\w_-]+(/(m|r)/[\\w_-]+)?">[\\w_-]+</a>`,
    'g'
  )
  const mentions = input.match(mentionRegex)
  let i = 0
  let formattedInput = input.replace(mentionRegex, () => `@${i++}`)

  formattedInput = formattedInput
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(
      /(^|[\s>]+)([\w]+?:\/\/[\w#$%&~/.\-;:=,?!@[\]+]*)/gi,
      '$1<a href="$2">$2</a>'
    )
    .replace(
      /(^|[\s>]+)((www|ftp)\.[\w#$%&~/.\-;:=,?!@[\]+]*)/gi,
      '$1<a href="https://$2">$2</a>'
    )
    .replace(
      /(^|[\s>]+)([a-z0-9\-_.]+)@([^[,<\s]+)/gi,
      '$1<a href="mailto:$2@$3">$2@$3</a>'
    )

  if (!Settings.value.allowLinks) {
    formattedInput = stripLinks(formattedInput, true)
  }

  // mod.io links
  formattedInput = formattedInput.replace(
    new RegExp(`>${MODIO_SITE}/(?:u|g)/([\\w_-]+(/(m|r)/[\\w_-]+)?)/?<`, 'g'),
    (_, p1) => `>@${p1.split('/').slice(-1)}<`
  )

  // Youtube embed
  formattedInput = formattedInput.replace(
    /<a href="https?:\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)([\w\-_]*)[^"]*">[^<]+<\/a>/gi,
    '<iframe width="560" height="315" src="https://www.youtube.com/embed/$1" frameborder="0" allowfullscreen></iframe>'
  )

  // Add mentions back in
  formattedInput = formattedInput.replace(/@\d+/g, (match) => {
    const index = Number(match.slice(1))
    if (mentions?.[index]) {
      return mentions[index]
        .replace(/>([\w_-]+)</, '>@$1<')
        .replace(MODIO_SITE, '#')
    }
    return match
  })

  return sanitize(formattedInput)
}

export function addBlankTargets(text) {
  return sanitize(
    ('' + text).replace(
      new RegExp(`href="${MODIO_SITE}/g/${game.value.name_id}/([rm])`, 'g'),
      `href="${hostUrl.value}#/$1`
    )
  )
}

export function stripLinks(text, preserveModioLinks) {
  return ('' + text).replace(/(<a [^>]*>)(.*?)(<\/a>)/gi, (match, _, p2) => {
    return preserveModioLinks && text.includes(`href="${MODIO_SITE}`)
      ? match
      : `<u>${p2}</u>`
  })
}

export function formatAgreement(text, noLinks = false) {
  let result = text.replace(
    /<center>(.*?)<\/center>/g,
    '<div class="tw-m-auto tw-w-fit">$1</div>'
  )

  AGREEMENT_TYPE_OPTIONS.forEach(({ value, paths }) => {
    paths.forEach((path) => {
      const href = `https://mod.io/${path}`
      const regex = new RegExp(
        `<a\\s+href=["]${href}["][^>]*>(.*?)<\\/a>`,
        'gi'
      )
      result = result?.replace(
        regex,
        `<a href="${href}" @click.prevent="openAgreementModal(${value})">$1</a>`
      )
    })
  })

  // removes any existing target & rel attributes to prevent vue compile warning
  result = result
    ?.replace(new RegExp('target=["](.*?)["]', 'gi'), '')
    ?.replace(new RegExp('rel=["](.*?)["]', 'gi'), '')

  if (noLinks) {
    return stripLinks(text)
  }

  return ('' + result).replace(
    /<a\s+href=/gi,
    '<a target="_blank" rel="noreferrer noopener" href='
  )
}
